import { ERoleCode, ERoleDisplayName } from 'types/enumItems';
import { TPrescriptionListItem } from 'types/textVisit';

export enum EPortalStatus {
    NOT_INVITED = 'NOT_INVITED',
    SIGNED_UP = 'Signed up',
    PENDING = 'Pending',
    INACTIVE = 'Inactive',
    DISABLED = 'Disabled',
}

export type TTeamMember = {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    tel: string;
    role: ERoleCode;
    permissions: string[];
    portalStatus: EPortalStatus;
    avatar?: string;
    providerAssigned?: string;
    isAdmin?: boolean;
    providerId?: number;
};

export type TTeamMemberPractice = {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    tel: string;
    role: ERoleCode;
    permissions: string[];
    portalStatus: EPortalStatus;
    specialties: string[];
    states: string[];
};

// api response types

export type TAssignedProvider = {
    firstName: string;
    lastName: string;
    providerId: number;
};

export type TTeamMemberDto = {
    id: number;
    userId?: number;
    providerId?: number | null;
    firstName: string;
    lastName: string;
    email: string;
    roleId: number;
    roleCode: ERoleCode;
    isAdmin: boolean;
    isAllProviders: boolean;
    isDefault: boolean;
    isOwner: boolean;
    phoneNumber?: string | null;
    portalStatus: string;
    status: string;
    lastActiveAt?: string | null;
    assignedProviders: TAssignedProvider[];
    listingStatusChangeAt?: string | null;
    listingReactivateAt?: string | null;
    listingStatus?: string | null;
};

export type TTeamMemberListPageParams = {
    current?: number;
    size?: number;
    roleCodes?: string[];
    providerIds?: number[];
    allProviders?: boolean;
    portalStatuses?: string[];
    statuses?: string[];
    orderKey?: string;
    orderType?: string;
};

// api request types

export type TAddStaff = {
    email: string;
    firstName: string;
    lastName: string;
    roleId: number;
    isAdmin?: boolean;
    phoneNumber?: string;
    providerIds?: number[];
};

export type TInviteProviderItem = {
    email: string;
    firstName: string;
    lastName: string;
    credential: string;
    gender: string;
    isAdmin: boolean;
};

export type TInviteProvidersParams = {
    providers: TInviteProviderItem[];
    sendInvite: boolean;
    ehrOnly?: boolean;
};

export type TReassignProvider = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    tel: string;
    role: ERoleCode;
    providerId: number;
    avatar?: string;
    specialties: string[];
    licensedStates: string[];
    availableAtVisitTime: boolean;
    nextAvailableSlot?: string;
};

export type TRole = {
    id: number;
    name: string;
    createBy?: number;
    createTime?: string;
    updateBy?: number;
    updateTime?: string;
    displayName: ERoleDisplayName;
};

export enum EImportRowErrorCode {
    EMAIL_MISSING = 'EMAIL_MISSING',
    EMAIL_INVALID = 'EMAIL_INVALID',
    FIRST_NAME_MISSING = 'FIRST_NAME_MISSING',
    DUPLICATE_IN_FILE = 'DUPLICATE_IN_FILE',
    EXISTING_USER = 'EXISTING_USER',
    ALREADY_IN_ORG = 'ALREADY_IN_ORG',
    ALREADY_PROVIDER = 'ALREADY_PROVIDER',
    INVALID = 'INVALID',
}

export enum ECheckEmailStatus {
    ADDABLE = 'ADDABLE',
    REACTIVATE = 'REACTIVATE',
    ALREADY_PROVIDER = 'ALREADY_PROVIDER',
    ALREADY_IN_ORG = 'ALREADY_IN_ORG',
    INVALID = 'INVALID',
}

export type TCheckEmailResult = {
    email: string;
    addable: boolean;
    status: ECheckEmailStatus;
    message: string | null;
};

export type TImportDraftRow = {
    id: number;
    rowIndex: number;
    firstName: string;
    lastName: string;
    email: string;
    credential: string;
    specialty: string;
    serviceStates: string;
    paymentMethod: string;
    status: string;
    rowErrorCode?: EImportRowErrorCode;
    rowErrorMsg?: string;
    confirmedAt?: string;
    confirmedMemberId?: number;
    inviteSentCount: number;
    invitedAt?: string;
};

export type TUpdateImportDraftReq = {
    credential?: string;
    email?: string;
    firstName?: string;
    isAdmin?: boolean;
    lastName?: string;
    paymentMethod?: string;
    serviceStates?: string;
    specialty?: string;
};

export type TConfirmBatchRow = {
    rowId: number;
};

export type TConfirmBatchParams = {
    rows: TConfirmBatchRow[];
};

export type TConfirmRowResult = {
    rowId: number;
    email: string;
    status: string;
    confirmedMemberId?: number;
    rowErrorCode?: string;
    rowErrorMsg?: string;
};

export type TConfirmBatchSummary = {
    total: number;
    created: number;
    alreadyInOrg: number;
    alreadyProvider: number;
    discarded: number;
    invalid: number;
};

export type TConfirmBatchResponse = {
    providerIdList: number[];
    results: TConfirmRowResult[];
    summary: TConfirmBatchSummary;
};

export type TImportDraftListParams = {
    page?: number;
    size?: number;
    status?: string;
};

export type TImportBatchStats = {
    total: number;
    draft: number;
    confirmed: number;
    discarded: number;
    errored: number;
    invited: number;
};

export type TImportBatchDetail = {
    batchUid: string;
    fileName: string;
    fileSize: number;
    sourceType: string;
    parseStatus: string;
    parseError?: string;
    uploadedAt: string;
    uploadedBy: string;
    doneAt?: string;
    stats: TImportBatchStats;
};

export type TGroupProvider = {
    providerId: number;
    avatar: string;
    name: string;
    email: string;
    specialties: string[];
    states: string;
    isAdmin?: boolean;
};

export type TPractice = {
    orgId: number;
    organizationName: string;
    roleCode: string;
    isAdmin: boolean;
    isDefault: boolean;
    providers: TGroupProvider[];
};

export type TCurrentUserPortalStatus = 'SIGNED_UP' | 'PENDING' | 'ACTIVE' | 'PAUSED' | 'DISABLED';

export type TProviderScopeItem = {
    providerId: number;
    name: string;
    email: string;
    avatar: string;
    specialties: string;
    states: string;
};

// One organization the user belongs to. Populated by the OPS currentUser-by-providerId endpoint.
export type TPracticeItem = {
    orgId: number;
    organizationName: string;
    roleCode: string;
    isAdmin: boolean;
    isDefault: boolean;
    providers: TProviderScopeItem[];
};

export type TCurrentUser = {
    user: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        tel: string;
    };
    //acting user 和 provider是同一个用户，通过email匹配
    actingUser: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        tel: string;
    };
    provider: {
        id: number;
        name: string;
        specialties: string;
        credential: string;
        gender: string;
    };
    roleCode: ERoleCode;
    isAdmin: boolean;
    teamMemberId: number;
    organizationId: number;
    portalStatus: TCurrentUserPortalStatus;
    redirectTo: string;
    actingMode: string;
    groupPractice: boolean;
    impersonated: boolean;
    // Every org the user belongs to (all statuses). Populated by the OPS by-providerId endpoint.
    practiceList?: TPracticeItem[];
};

// {
//     "token": "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJsb2dpblR5cGUiOiJsb2dpbiIsImxvZ2luSWQiOjU3MzgsInJuU3RyIjoiVXdMcjBIMzZMNE9XTktRbWNHSE13ZGp2eVd1RmZ3QXgiLCJ1c2VyVHlwZSI6IlBST1ZJREVSX1BPUlRBTCIsImZ1bGxOYW1lIjoiZXZhbjAyIHN0YWZmIiwiYWN0aW5nTW9kZSI6IkRFTEVHQVRFRCIsImFjdGluZ1VzZXJJZCI6NDY5Nywib3JnYW5pemF0aW9uSWQiOjUwMiwicm9sZUNvZGUiOiJHUk9VUF9BRE1JTklTVFJBVElWRV9BU1NJU1RBTlQiLCJpc0FkbWluIjpmYWxzZSwiaXNHcm91cFByYWN0aWNlIjp0cnVlLCJwcm92aWRlcklkIjo1MX0.SmNPFuGxpADgnqr2bXcqrp8dydXaH2ECTKyZQXkgejw",
//     "needPracticeSelect": false,
//     "user": {
//         "id": 5738,
//         "firstName": "evan02",
//         "lastName": "staff",
//         "email": "evanliu.it+staff02@gmail.com",
//         "tel": "1212121222"
//     },
//     "actingUser": {
//         "id": 4697,
//         "firstName": "Xiaocong ",
//         "lastName": "Liu ne",
//         "email": "xiaocong.liu@helloklarity.com",
//         "tel": null
//     },
//     "provider": {
//         "id": 51,
//         "name": "Xiaocong  Liu ne",
//         "specialties": "Psychiatry,Pain Management,Interventional Cardiology,Nephrology,Trauma Surgery",
//         "credential": "ACCNS-P,ACCNS-N,ACNP,ACHPN,A-GNP",
//         "gender": "Female"
//     },
//     "roleCode": "GROUP_ADMINISTRATIVE_ASSISTANT",
//     "isAdmin": false,
//     "teamMemberId": 502,
//     "organizationId": 502,
//     "portalStatus": "SIGNED_UP",
//     "redirectTo": "PORTAL_HOME",
//     "actingMode": "DELEGATED",
//     "impersonator": {
//         "id": 5738,
//         "name": "evan02 staff",
//         "type": "TEAM_MEMBER",
//         "level": null
//     },
//     "groupPractice": true,
//     "impersonated": true
// }

export type TSwitchProviderResponse = {
    token: string;
    needPracticeSelect: boolean;
    authPaylod?: any;
    user: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        tel: string;
    };
    provider: {
        id: number;
        name: string;
        specialties: string;
        credential: string;
        gender: string;
    };
    roleCode: ERoleCode;
    isAdmin: boolean;
    teamMemberId: number;
    organizationId: number;
    portalStatus: TCurrentUserPortalStatus;
    redirectTo: string;
    actingMode: string;
    groupPractice: boolean;
    impersonated: boolean;
};

export type TPracticePrescriptionListFilter = {
    current: number;
    size: number;
    drugName?: string;
    orderKey?: string;
    orderType?: string;
    patientName?: string;
    providerId?: number;
    providerName?: string;
    sortBy?: string;
    sortType?: string;
    status?: string[];
};

export type TPracticePrescriptionItem = TPrescriptionListItem & {
    id: number;
    providerId: number;
    providerFirstName: string;
    providerLastName: string;
    providerCredential: string;
    ssClientId: number;
};

export enum EViewType {
    GROUP_PRACTICE = 'GROUP_PRACTICE',
    PROVIDER_SELF = 'PROVIDER_SELF',
}

export type TReassignExecuteReq = {
    newProviderId?: number;
    newStartAt: string;
    newEndAt: string;
    notifyNewProvider: boolean;
    notifyOriginalProvider: boolean;
    notifyPatient: boolean;
    priceDiffAction?: string;
    reasonCode?: string;
    reasonText?: string;
};

export type TReassignExecuteRsp = {
    appointmentId: number;
    appointmentStatus: string;
    newProviderId: number;
    notifications: string[];
    priceDiffAction: string;
    reassignHistoryId: number;
    servicePriceDiffCents: number;
    timeChanged: boolean;
};

export type TTimeslot = {
    badge?: 'AVAILABLE_AT_TIME' | 'LATER_SAME_DAY' | 'ANOTHER_DAY';
    startAt?: string;
    endAt?: string;
};

export type TEligibleProvider = {
    providerId: number;
    firstName: string;
    lastName: string;
    credential?: string;
    specialty?: string;
    avatar?: string;
    state?: string;
    available: boolean;
    supportPayType?: string;
    newServicePriceCents?: number;
    priceCompare?: string;
    alternativeSlots?: TTimeslot[];
};

export type TReassignScenarioRsp = {
    scenario: string;
    payType: string;
    tz: string;
    originalServicePriceCents?: number;
    blockReason?: string;
    availableProviders: TEligibleProvider[];
    unavailableProviders: TEligibleProvider[];
};

export type TScopedProviderOptionsParams = {
    includeSelf?: boolean;
    keyword?: string;
};

export type TScopedProviderOption = {
    providerId: number;
    userId: number;
    firstName: string;
    lastName: string;
    credential: string;
    // provider_uniprofile_base.photo, null when no uniprofile photo is set
    avatar: string | null;
    npi?: string;
    states?: string[];
    roleCode: 'GROUP_OWNER' | 'GROUP_PROVIDER' | string;
    isSelf: boolean;
    // uid_provider.uid_public, null when the provider has no uid_provider row
    uidPublic?: string | null;
};
